import { useState } from 'react';
import { Box, Button, Divider, FormControl, FormGroup, Modal, Stack, TextField, Typography } from '@mui/material';
import { Close, Photo } from '@mui/icons-material';
import { modalStyle } from '@/components/modalStyle';
import { uploadFile } from '@/utils/api-helpers';
import { ImageCropComponent } from '@/components/images/ImageCropComponent';
import { FileInputButton } from '@/components/FileInput';
import { objectHasId } from '@/utils/create-mode-helper';
import { ImageAdder } from '@/components/ImageAdder';
import { CreateGlossaryTermDTO, GlossaryTermDTO } from '@/generated';
import { ExternalLinksComponent } from '../exercises/elements/ExternalLinksComponent';
import { useCreateGlossaryTerm, useUpdateGlossaryTerm } from '@/hooks/glossary-mutation.hooks';

export interface UpdateGlossaryTermModalProps {
  editGlossaryTerm?: GlossaryTermDTO | CreateGlossaryTermDTO;
  setEditGlossaryTerm: (e?: GlossaryTermDTO | CreateGlossaryTermDTO) => void;
  glossaryTermUpdated?: (gt: GlossaryTermDTO) => {};
}

export const UpdateGlossaryTermModal = (props: UpdateGlossaryTermModalProps) => {
  const { editGlossaryTerm: glossaryTerm, setEditGlossaryTerm: setGlossaryTerm } = props;
  //TODO: UseReducer?
  const [additionalImageFiles, setAdditionalImageFiles] = useState<Array<File>>([]);
  const [imageFile, setImageFile] = useState<File | null>();
  const isEditMode = glossaryTerm ? objectHasId(glossaryTerm) : false;
  const createGlossaryTermMutation = useCreateGlossaryTerm();
  const updateGlossaryTermMutation = useUpdateGlossaryTerm();

  return glossaryTerm ? (
    <Modal sx={{ marginTop: 5 }} open={glossaryTerm !== undefined} onClose={() => setGlossaryTerm()}>
      <Box sx={{ ...modalStyle }}>
        <Box sx={{ my: 1 }}>
          <Stack direction={'row'}>
            <Typography variant="h5">{isEditMode ? 'Edit Glossary Term' : 'Create Glossary Term'} </Typography>
            <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
              <Close
                onClick={() => {
                  setGlossaryTerm(undefined);
                }}
              />
            </Box>
          </Stack>
        </Box>
        <Stack>
          <TextField
            value={glossaryTerm?.name}
            placeholder="Title"
            label="Title"
            aria-label="Title"
            aria-describedby="basic-addon1"
            sx={{ my: 1 }}
            onChange={(e: any) => {
              if (glossaryTerm) setGlossaryTerm({ ...glossaryTerm, name: e.target.value });
            }}
          />
          <Box sx={{ marginTop: 2, marginBottom: 2 }}>
            <ImageCropComponent imageFile={imageFile} onImageFileChanged={setImageFile} imageId={glossaryTerm.imageId} />
          </Box>
          <Box sx={{ mt: 0, mb: 3, columnGap: 2, display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
            <FileInputButton accept=".png,.jpg,.jpeg" startIcon={<Photo />} onFilePicked={(f) => setImageFile(f)}>
              Change Image
            </FileInputButton>
          </Box>
          <TextField
            label={'Description'}
            multiline
            sx={{ mb: 2 }}
            maxRows={5}
            defaultValue={glossaryTerm.description}
            value={glossaryTerm?.description}
            onChange={(e: any) => {
              if (glossaryTerm) setGlossaryTerm({ ...glossaryTerm, description: e.target.value });
            }}
          />

          <FormControl sx={{ my: 1 }} component="fieldset" variant="standard">
            <FormGroup row></FormGroup>
          </FormControl>
        </Stack>
        <ExternalLinksComponent objectWithLinks={glossaryTerm} setObjectsWithLinks={setGlossaryTerm} />

        <Typography sx={{ my: 1 }}>Additional Images</Typography>
        <ImageAdder
          imageFileIds={glossaryTerm.additionalImageIds ?? []}
          filesUpdated={(files) => {
            setAdditionalImageFiles(files);
          }}
          fileIdsUpdated={(ids) => {
            setGlossaryTerm({ ...glossaryTerm, additionalImageIds: ids });
          }}
        />
        <Divider sx={{ m: 1 }} />

        <Box sx={{ m: 1 }}>
          <Button
            onClick={async () => {
              if (glossaryTerm !== undefined) {
                let ex = glossaryTerm;
                if (imageFile) {
                  const fileId = await uploadFile(imageFile);
                  ex = { ...glossaryTerm, imageId: fileId };
                }
                if (additionalImageFiles.length) {
                  let fileIds = await Promise.all(additionalImageFiles.map((f) => uploadFile(f)));
                  ex = { ...glossaryTerm, additionalImageIds: [...(glossaryTerm.additionalImageIds ?? []), ...fileIds] };
                }

                if (objectHasId(ex)) {
                  await updateGlossaryTermMutation.mutateAsync(ex);
                  setImageFile(null);
                  setAdditionalImageFiles([]);
                } else {
                  await createGlossaryTermMutation.mutateAsync(ex);
                  setAdditionalImageFiles([]);
                  setImageFile(null);
                }
              }

              setGlossaryTerm(undefined);
            }}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
          <Button
            onClick={() => {
              setGlossaryTerm(undefined);
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  ) : (
    <></>
  );
};
